/**
 * useFrozenLeftColumns Hook
 *
 * Manages the number of left-frozen (sticky) data columns for ArTable.
 * Persists to localStorage keyed by tableId.
 */

import { useCallback, useState } from 'react';

export type FrozenLeftColumnsCount = 0 | 1 | 2 | 3;

type UseFrozenLeftColumnsReturn = {
  frozenLeftColumns: FrozenLeftColumnsCount;
  setFrozenLeftColumns: (count: FrozenLeftColumnsCount) => void;
};

const getStorageKey = (tableId: string): string => `arTable-frozenLeftColumns-${tableId}`;

const loadFromStorage = (tableId: string): FrozenLeftColumnsCount => {
  try {
    const stored = localStorage.getItem(getStorageKey(tableId));
    const parsed = stored ? JSON.parse(stored) : null;
    if (parsed === 0 || parsed === 1 || parsed === 2 || parsed === 3) return parsed;
    return 3;
  } catch {
    return 3;
  }
};

const saveToStorage = (tableId: string, count: FrozenLeftColumnsCount): void => {
  try {
    localStorage.setItem(getStorageKey(tableId), JSON.stringify(count));
  } catch {
    // Storage unavailable, ignore
  }
};

export const useFrozenLeftColumns = (tableId: string): UseFrozenLeftColumnsReturn => {
  const [frozenLeftColumns, setFrozenLeftColumnsState] = useState<FrozenLeftColumnsCount>(
    () => loadFromStorage(tableId)
  );

  const setFrozenLeftColumns = useCallback(
    (count: FrozenLeftColumnsCount) => {
      setFrozenLeftColumnsState(count);
      saveToStorage(tableId, count);
    },
    [tableId]
  );

  return { frozenLeftColumns, setFrozenLeftColumns };
};

